import { BillingSettings } from "@/components/settings/billing-settings";
import { CompanySettings } from "@/components/settings/company-settings";
import { DepartmentsSettings } from "@/components/settings/departments-settings";
import { UserSettings } from "@/components/settings/user-settings";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { sessionQueryOptions } from "@/lib/query-options";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";

const settingsSearchSchema = z.object({
  tab: z.enum(["user", "company", "departments", "billing", "support"]),
});

export const Route = createFileRoute(
  "/(dashboard)/_authenticated/$companyId/settings",
)({
  component: SettingsComponent,
  validateSearch: zodValidator(settingsSearchSchema),
});

function SettingsComponent() {
  const { data: session } = useSuspenseQuery(sessionQueryOptions);
  const user = session?.user;
  if (!user) throw new Error("No session found");

  const { companyId } = useParams({ strict: false });
  const search = useSearch({
    from: "/(dashboard)/_authenticated/$companyId/settings",
  });
  const navigate = useNavigate({ from: Route.fullPath });

  const currentCompany = user.companies.find((c) => c.id === companyId);
  const isCompanyAdmin = currentCompany?.isAdmin;
  const isCompanyOwner = currentCompany?.isOwner;

  const isCompanyManager = user.departments
    .filter((d) => d.companyId === companyId)
    .some((d) => d.role === "manager");

  return (
    <div className="container mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Configuración</h1>
        <p className="text-muted-foreground">
          Gestiona tu cuenta, empresa y configuración de la aplicación
        </p>
      </div>

      <Tabs
        value={search.tab}
        onValueChange={(value) =>
          navigate({
            search: {
              tab: value as "user" | "company" | "departments" | "billing",
            },
          })
        }
        className="w-full"
      >
        <div className="border-b w-full">
          <TabsList className="bg-transparent p-0">
            <TabsTrigger
              value="user"
              className="relative rounded-md border-b-2 border-transparent px-4 py-1 font-medium text-muted-foreground hover:text-foreground data-[state=active]:bg-muted data-[state=active]:text-foreground"
            >
              Usuario
            </TabsTrigger>
            {isCompanyAdmin && (
              <TabsTrigger
                value="company"
                className="relative rounded-md border-b-2 border-transparent px-4 py-1 font-medium text-muted-foreground hover:text-foreground data-[state=active]:bg-muted data-[state=active]:text-foreground"
              >
                Empresa
              </TabsTrigger>
            )}
            {(isCompanyManager || isCompanyAdmin) && (
              <TabsTrigger
                value="departments"
                className="relative rounded-md border-b-2 border-transparent px-4 py-1 font-medium text-muted-foreground hover:text-foreground data-[state=active]:bg-muted data-[state=active]:text-foreground"
              >
                Departamentos
              </TabsTrigger>
            )}
            {isCompanyOwner && (
              <TabsTrigger
                value="billing"
                className="relative rounded-md border-b-2 border-transparent px-4 py-1 font-medium text-muted-foreground hover:text-foreground data-[state=active]:bg-muted data-[state=active]:text-foreground"
              >
                Facturación
              </TabsTrigger>
            )}
            <TabsTrigger
              value="support"
              className="relative rounded-md border-b-2 border-transparent px-4 py-1 font-medium text-muted-foreground hover:text-foreground data-[state=active]:bg-muted data-[state=active]:text-foreground"
            >
              Soporte
            </TabsTrigger>
            {/*<TabsTrigger
            value="forms"
            className="relative rounded-md border-b-2 border-transparent px-4 py-1 font-medium text-muted-foreground hover:text-foreground data-[state=active]:bg-muted data-[state=active]:text-foreground"
          >
            Formularios
          </TabsTrigger>*/}
          </TabsList>
        </div>

        <div className="p-6">
          <TabsContent value="user">
            <UserSettings />
          </TabsContent>
          {isCompanyAdmin && (
            <TabsContent value="company">
              <CompanySettings />
            </TabsContent>
          )}
          {(isCompanyManager || isCompanyAdmin) && (
            <TabsContent value="departments">
              <DepartmentsSettings />
            </TabsContent>
          )}
          {isCompanyOwner && (
            <TabsContent value="billing">
              <BillingSettings />
            </TabsContent>
          )}
          <TabsContent value="support">
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold">Soporte</h2>
                <p className="text-sm text-muted-foreground">
                  ¿Necesitas ayuda? Escríbenos a info@hsechain.com
                </p>
              </div>
              <a
                href={`mailto:info@hsechain.com?subject=${encodeURIComponent("Soporte HSEChain")}&body=${encodeURIComponent("Hola, necesito ayuda con...")}`}
                className="inline-flex items-center rounded-md border px-3 py-2 text-sm font-medium hover:bg-muted transition-colors"
              >
                Contacta
              </a>
            </div>
          </TabsContent>
          {/*<TabsContent value="forms">
            <FormSettings />
          </TabsContent>*/}
        </div>
      </Tabs>
    </div>
  );
}
